import React from "react";
import { motion } from "framer-motion";
import { Timer, MapPin, Shuffle, Backpack } from "lucide-react";

const features = [
  {
    icon: Timer,
    title: "Easy to learn",
    body: "Playing in under a minute.",
    bg: "var(--pw-yellow)",
    fg: "var(--pw-navy)",
  },
  {
    icon: MapPin,
    title: "Play almost anywhere",
    body: "If there are people around, there's a game.",
    bg: "var(--pw-pink)",
    fg: "#fff",
  },
  {
    icon: Shuffle,
    title: "Different every time",
    body: "The people around you create the game.",
    bg: "var(--pw-green)",
    fg: "#fff",
  },
  {
    icon: Backpack,
    title: "Made to go with you",
    body: "Perfect for airports, pubs, cafés and holidays.",
    bg: "var(--pw-orange)",
    fg: "#fff",
  },
];

export const WhyLoveIt = () => {
  return (
    <section
      id="why-love"
      data-testid="why-love-it-section"
      className="confetti-canvas py-14 md:py-28 relative"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 relative z-10">
        <div className="mb-8 md:mb-16 max-w-3xl">
          <p className="eyebrow mb-4" style={{ color: "var(--pw-yellow)" }}>Why You&apos;ll Love It</p>
          <h2
            className="font-display font-black leading-[0.95] tracking-tighter text-white"
            style={{ fontSize: "clamp(2rem, 5.5vw, 4.5rem)" }}
          >
            Small box.
            <br />
            <span className="yellow-stroke" style={{ color: "#fff" }}>Unreasonable joy.</span>
          </h2>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5">
          {features.map((f, i) => {
            const Icon = f.icon;
            const isLight = f.fg !== "#fff";
            return (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                data-testid={`why-item-${i}`}
                className="relative rounded-2xl p-5 md:p-8 card-tilt overflow-hidden"
                style={{ backgroundColor: f.bg, color: f.fg, minHeight: "160px" }}
              >
                <div
                  className="w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center mb-3 md:mb-4"
                  style={{
                    backgroundColor: isLight ? "var(--pw-navy)" : "rgba(255,255,255,0.18)",
                    color: isLight ? "var(--pw-yellow)" : "#fff",
                  }}
                >
                  <Icon className="w-4 h-4 md:w-5 md:h-5" />
                </div>
                <h3 className="font-display font-bold tracking-tight mb-1.5 md:mb-2 text-lg md:text-2xl">
                  {f.title}
                </h3>
                <p
                  className="text-sm md:text-base leading-relaxed"
                  style={{ color: isLight ? "rgba(14,26,43,0.75)" : "rgba(255,255,255,0.85)" }}
                >
                  {f.body}
                </p>
              </motion.div>
            );
          })}
        </div>

        <p className="mt-6 md:mt-8 text-white/60 text-xs md:text-base max-w-xl">
          Made for friends, couples and families — fun across generations.
        </p>
      </div>
    </section>
  );
};

export default WhyLoveIt;
